(ns ui.editor2.editor1-import
  "Logic to import a design from editor1 into editor2."
  (:require [app.util.transit :as transit]
            [datascript.core :as ds]
            [storrito.instagram.story.editor.model :as editor.model]
            [storrito.video.config :as video-config]
            [ui.editor2.schema :as schema]
            [ui.editor2.common :as common]
            ["mime-types" :as mime]
            [clojure.string :as str]
            [clojure.set :as set]
            [lambdaisland.uri :as uri]
            [cljs.core.async :refer [<!]]
            [ui.editor2.defaults :as defaults])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(defn transact
  [state tx]
  (update state
          :db
          (fn [db]
            (:db-after
             (ds/with db
                      tx)))))

(defn add-transit-str
  "Fetches the editor1 design of `:story-media/eid` as a transit string.
   A non-ok response rejects with its status: handing an error page like
   `Internal server error` to `transit-decode` fails with a confusing
   JSON.parse SyntaxError instead (STORRITO-V2-MB)."
  [w]
  (-> (str "/api/story-media/get?story-media-eid="
           (:story-media/eid w))
      (js/fetch)
      (.then (fn [response]
               (if (.-ok response)
                 (.text response)
                 (js/Promise.reject
                   (js/Error. (str "Fetch failed: " (.-status response)
                                   " for story-media " (:story-media/eid w)))))))
      (.then (fn [transit-str]
               (assoc w
                      :transit-str
                      transit-str)))))

(defn add-editor1
  [w]
  (assoc w
         :editor1
         (transit/transit-decode (:transit-str w))))

(defn add-empty-db
  [w]
  (assoc w
         :db
         @(ds/create-conn schema/schema)))

(defn add-item
  [w]
  (assoc w
         :item
         (get-in w
                 [:editor1
                  :items
                  0])))

(defn get-slide-duration
  [w]
  (get-in w
          [:slide
           :slide/duration]))

(defn main-video-duration
  "The `:asset/duration` of the main video, if the server knows the
   duration of the source video. `add-asset-durations` then does not
   need to load the video to read its duration, which costs a far away
   user multiple round trips. Without a `:src-duration` the asset stays
   without duration and `add-asset-durations` measures it."
  [main-video]
  (when-let [src-duration (:src-duration main-video)]
    {:asset/duration (common/s->ms src-duration)}))

(defn known-duration-ms
  "`seconds` as milliseconds, nil unless it is a positive number.
   `:story-upload/save!` stores `:instagram.video/duration` 0.0 when the
   browser could not read the uploaded video, and other ways to create a
   story-media leave it out, so 0 means unknown (see
   `storrito.story-media.get/src-video-duration`)."
  [seconds]
  (when (and (number? seconds)
             (js/isFinite seconds)
             (pos? seconds))
    (common/s->ms seconds)))

(defn main-video-item-duration-ms
  "The length of the Story item the main video fills, in milliseconds,
   nil when the server knows no duration. Prefers the item's own
   `:duration` (the editor1 cut, at most `ig-story-max-duration`), else
   the source video's length, capped the same way. A `:duration` of 0
   (brain-138: a gallery video stored with duration 0.0 was imported as
   a 0 ms slide and rendered as a 0.033 s video, which Instagram
   refuses) counts as unknown."
  [main-video]
  (or (known-duration-ms (:duration main-video))
      (some-> (known-duration-ms (:src-duration main-video))
              (min (common/s->ms video-config/ig-story-max-duration)))))

(defn main-video-asset
  [{:keys [item]}]
  (let [main-video (:main-video item)
        object (get-in item
                       [:fabricjs-model
                        :objects
                        "main-video"])]
    (merge
      {:db/id "main-video"
       :asset/uuid (:blob main-video)
       :asset/type :video
       :asset/uri (str "/blob/"
                       (:blob main-video))
       :asset/name (str (:blob main-video)
                        (some->> main-video
                                 (:content-type)
                                 (.extension mime)
                                 (str ".")))
       :asset/width (:width object)
       :asset/height (:height object)
       ;; TODO: muted
       }
      (main-video-duration main-video))))

(defn clip-transform
  [object]
  {:clip/transform
   {:translate [(:left object)
                (:top object)
                0]
    :scale [(:scaleX object)
            (:scaleY object)
            1]
    :rotate (mod (:angle object)
                 360)}})

(defn main-video-clip
  [{:keys [item] :as w}]
  (let [main-video (:main-video item)
        object (get-in item
                       [:fabricjs-model
                        :objects
                        "main-video"])]
    (merge
      (clip-transform object)
      {:clip/uuid (random-uuid)
       :clip/asset "main-video"
       :clip/name "Main Video"
       :clip/start 0
       ;; Without a known duration the clip fills the slide, whose
       ;; default `add-slide` chose; `add-measured-main-video-duration`
       ;; corrects both once the video was measured.
       :clip/end (or (main-video-item-duration-ms main-video)
                     (get-slide-duration w))
       :clip/offset (if-let [start-time (:start-time main-video)]
                      (common/s->ms start-time)
                      0)
       :clip/track "main-video-track"})))

(defn add-main-video
  [{:keys [item] :as w}]
  (if (:main-video item)
    (let [uuid (random-uuid)]
      (-> w
          (transact [(main-video-asset w)
                     (main-video-clip w)
                     {:db/id "main-video-track"
                      :track/uuid uuid
                      :track/name "main track"
                      :track/object-id "main-video"}])))
    w))

(defn get-objects
  [w]
  (vals
    (get-in w
            [:item
             :fabricjs-model
             :objects])))

(defn extract-uuid
  [object-id]
  (some-> (re-find common/uuid-regex
                   (or object-id
                       ""))
          (parse-uuid)))

(defn gif?
  [object]
  (when-let [id (:id object)]
    (and (str/starts-with? id
                           "sticker-")
         (extract-uuid id))))

(defn contains-gifs?
  [w]
  (some gif?
        (get-objects w)))

(defn add-slide
  [w]
  (let [duration (or (main-video-item-duration-ms (get-in w
                                                          [:item
                                                           :main-video]))
                     (* (or (when contains-gifs?
                              video-config/ig-image-gif-duration)
                            6.0)
                        1000))
        slide {:slide/uuid (random-uuid)
               :slide/duration duration
               }]
    (-> w
        (transact [slide])
        (assoc :slide slide))))

(defn get-track-mapping
  [db]
  (into {}
        (map (fn [track]
               [(:track/object-id track)
                (:track/uuid track)]))
        (common/get-tracks db)))

(defn get-order
  [w]
  (-> ["background"
       "main-image"
       "main-video"]
      (concat (get-in w
                      [:item
                       :fabricjs-model
                       :order]))
      (distinct)))

(defn remove-track-object-ids-tx
  [db]
  (map (fn [track]
         [:db/retract
          [:track/uuid (:track/uuid track)]
          :track/object-id
          (:track/object-id track)
          ])
       (common/get-tracks db)))

(defn add-timeline
  [w]
  (let [track-mapping (get-track-mapping (:db w))
        order (get-order w)]
    (transact w
              (concat
                (remove-track-object-ids-tx (:db w))
                [{:timeline/uuid (random-uuid)
                  :timeline/tracks (vec (reverse
                                          (keep track-mapping
                                                order)))
                  :timeline/slides [(get-in w
                                            [:slide
                                             :slide/uuid])]}]))))

(defn image?
  [object]
  (when-let [id (:id object)]
    (and (str/starts-with? id
                           "image-")
         (extract-uuid id))))

(defn extract-canva-id
  [s]
  (when-let [[_ id] (re-find #"^com-canva-(.+)$" s)]
    id))

(defn emoji?
  [object]
  (when-let [id (:id object)]
    (and (str/starts-with? id
                           "emoji-")
         (extract-uuid id))))

(defn image-tx
  [{:keys [object] :as w}]
  (let [id (:id object)
        uuid (or (extract-uuid id)
                 (random-uuid))
        asset-data (into {}
                         (filter (fn [[_ v]] (seq v))
                                 {:legacy-filters (seq (:filters object))
                                  :canva/design-id (extract-canva-id id)}))]
    [(merge {:db/id "asset"
             :asset/uuid uuid
             :asset/type :image
             :asset/uri (:src object)
             :asset/name (str uuid)
             :asset/width (:width object)
             :asset/height (:height object)}
            (when (seq asset-data)
              {:asset/data asset-data}))
     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name (:id object)
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-images
  [w]
  (reduce
    (fn [w* object]
      (let [tx (-> w*
                   (assoc :object object)
                   (image-tx))]
        (-> w*
            (transact tx))))
    w
    (filter
      (fn [object]
        (or (= (:id object)
               "main-image")
            (image? object)
            (extract-canva-id (:id object))
            (emoji? object)))
      (get-objects w)))
  )

(defn add-blurry-background
  [w]
  (let [background (get-in w
                           [:item :fabricjs-model :objects "background"])]
    (if (seq (:filters background))
      (let [tx (-> w
                   (assoc :object background)
                   (image-tx))]
        (transact w tx))
      w)))

(defn gif-video-tx
  [{:keys [object] :as w}]
  (let [uuid (extract-uuid (:id object))]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :video
      :asset/uri (:video_src object)
      :asset/name (str uuid)
      :asset/width (:width object)
      :asset/height (:height object)
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name (:id object)
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/loop true
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn gif-video?
  [object]
  (when-let [id (:id object)]
    (and (str/starts-with? id
                           "gif-")
         (extract-uuid id))))

(defn add-gif-videos
  [w]
  (reduce
    (fn [w* object]
      (let [tx (-> w*
                   (assoc :object object)
                   (gif-video-tx))]
        (-> w*
            (transact tx))))
    w
    (filter
      gif-video?
      (get-objects w)))
  )

(defn gif-tx
  [{:keys [object] :as w}]
  (let [uuid (extract-uuid (:id object))]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :gif
      :asset/uri (:video_src object)
      :asset/name (str uuid)
      :asset/width (:width object)
      :asset/height (:height object)
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name (:id object)
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/loop true
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-gifs
  [w]
  (reduce
    (fn [w* object]
      (let [tx (-> w*
                   (assoc :object object)
                   (gif-tx))]
        (-> w*
            (transact tx))))
    w
    (filter
      gif?
      (get-objects w)))
  )

(defn prefixed-uuid
  [prefix]
  (fn [object]
    (when-let [id (:id object)]
      (and (str/starts-with? id
                             prefix)
           (extract-uuid id)))))

(defn get-prefix-objects
  [w]
  (filter
    (prefixed-uuid (:prefix w))
    (get-objects w)))

(defn countdown-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :countdown-sticker
      :asset/name "Countdown Sticker"
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (-> object
                      (:countdown)
                      (update :end_ts
                              (fn [epoch-sec]
                                (let [min-date (+ (.getTime (js/Date.))
                                                  (* 1000 60 60))]
                                  (when epoch-sec
                                    (js/Date. (max
                                                min-date
                                                (* epoch-sec
                                                   1000))))))))
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name (:id object)
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-countdown
  [w]
  (reduce
    (fn [w* object]
      (let [tx (-> w*
                   (assoc :object object)
                   (countdown-tx))]
        (-> w*
            (transact tx))))
    w
    (filter
      (fn [object]
        (= (:id object)
           "countdown-sticker"))
      (get-objects w)))
  )

(defn poll-sticker-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :poll-sticker
      :asset/name "Poll Sticker"
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (:poll object)
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name "Poll Sticker"
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-poll-stickers
  [w]
  (reduce
    (fn [w* object]
      (let [tx (-> w*
                   (assoc :object object)
                   (poll-sticker-tx))]
        (-> w*
            (transact tx))))
    w
    (filter
      (fn [object]
        (= (:id object)
           "poll-sticker-v2"))
      (get-objects w)))
  )

(defn get-assets-without-duration
  [db]
  (filter
    (fn [asset]
      (not (:asset/duration asset)))
    (common/get-all-assets db)))

(defn skip-unreadable-duration
  "Resolves `duration-tx` to nil instead of rejecting when the browser
   cannot load the asset (e.g. a video codec that Safari does not
   support). The editor handles an asset without `:asset/duration`, so
   one unreadable asset must not fail the whole import."
  [duration-tx asset]
  (.catch duration-tx
          (fn [error]
            (js/console.warn "Skipping the duration of asset"
                             (:asset/uri asset)
                             error)
            nil)))

(defn get-asset-duration-tx
  [w]
  (let [assets (get-assets-without-duration (:db w))]
    (js/Promise.all
      (keep
        (fn [asset]
          (let [callback (fn [{:keys [ms]}]
                           {:asset/uuid (:asset/uuid asset)
                            :asset/duration ms})]
            (some-> (case (:asset/type asset)
                      :video
                      (.then (common/get-video-length {:url (:asset/uri asset)})
                             callback)
                      :gif
                      (.then (common/get-gif-length {:url (:asset/uri asset)})
                             callback)
                      ;; else:
                      nil
                      )
                    (skip-unreadable-duration asset))))
        assets))))

(defn add-asset-durations
  [w]
  (.then (get-asset-duration-tx w)
         (fn [tx]
           (transact w
                     (into [] (remove nil?) tx)))))

(defn measured-main-video-duration-tx
  "Sets the slide, and every clip that fills it, to the measured length
   of the main video (at most `ig-story-max-duration`) when the server
   knew no duration, so that `add-slide`'s default does not decide how
   long the Story is. Nil when the server knew the duration or the video
   could not be measured either."
  [{:keys [item slide db]}]
  (let [main-video (:main-video item)
        measured (when (and main-video
                            (nil? (main-video-item-duration-ms main-video)))
                   (:asset/duration (ds/entity db
                                               [:asset/uuid (:blob main-video)])))]
    (when (and (number? measured)
               (pos? measured))
      (let [slide-duration (:slide/duration slide)
            duration (min measured
                          (common/s->ms video-config/ig-story-max-duration))]
        (into [{:slide/uuid (:slide/uuid slide)
                :slide/duration duration}]
              (map (fn [clip-eid]
                     [:db/add clip-eid :clip/end duration]))
              (ds/q '[:find [?clip ...]
                      :in $ ?slide-duration
                      :where
                      [?clip :clip/uuid _]
                      [?clip :clip/end ?slide-duration]]
                    db
                    slide-duration))))))

(defn add-measured-main-video-duration
  "Runs after `add-asset-durations`; the stickers added later take the
   corrected `get-slide-duration` as their `:clip/end`."
  [w]
  (if-let [tx (measured-main-video-duration-tx w)]
    (-> w
        (transact tx)
        (assoc-in [:slide :slide/duration]
                  (:slide/duration (first tx))))
    w))

(defn extract-hashtag-data
  [object]
  (let [i-text (some
                 (fn [obj]
                   (when (= (:type obj)
                            "i-text")
                     obj))
                 (:objects object))
        rect (some
               (fn [obj]
                 (when (= (:type obj)
                          "rect")
                   obj))
               (:objects object))]
    {:text (:text i-text)
     :scaleX 1
     :scaleY 1
     :top 0
     :left 0
     :type "hashtag"
     :design (merge {:text-props
                     (select-keys i-text
                                  [:fontFamily
                                   :fontSize
                                   :top
                                   :fill])}
                    (select-keys rect
                                 [:rx
                                  :ry
                                  :opacity]))}
    ))

(defn hashtag-sticker-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)
        hashtag-data (extract-hashtag-data object)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :hashtag-sticker
      :asset/name (:text hashtag-data)
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (extract-hashtag-data object)
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name (:text hashtag-data)
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn extract-text-data
  [object]
  (some
    (fn [obj]
      (when (= (:type obj)
               "i-text")
        obj))
    (:objects object)))

(defn text-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :text-sticker
      :asset/name (:text object)
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data object
      }
     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name (:text object)
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-texts
  [w]
  (reduce
    (fn [w* object]
      (let [tx (-> w*
                   (assoc :object object)
                   (text-tx))]
        (-> w*
            (transact tx))))
    w
    (get-prefix-objects (assoc w
                               :prefix "i-text"))))

(defn add-hashtag-stickers
  [w]
  (reduce
    (fn [w* object]
      (let [tx (-> w*
                   (assoc :object object)
                   (hashtag-sticker-tx))]
        (-> w*
            (transact tx))))
    w
    (get-prefix-objects (assoc w
                               :prefix "hashtag")))
  )

(defn extract-mention-data
  [object]
  (let [i-text (some
                 (fn [obj]
                   (when (= (:type obj)
                            "i-text")
                     obj))
                 (:objects object))
        rect (some
               (fn [obj]
                 (when (= (:type obj)
                          "rect")
                   obj))
               (:objects object))]
    {:text (:text i-text)
     :user-id (:user-id i-text)
     :type "usertag"
     :design (merge {:text-props
                     (select-keys i-text
                                  [:fontFamily
                                   :fontSize
                                   :top
                                   :fill])}
                    (select-keys rect
                                 [:rx
                                  :ry
                                  :opacity]))}
    ))

(defn mention-sticker-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)
        mention-data (extract-mention-data object)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :mention-sticker
      :asset/name (:text mention-data)
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (extract-mention-data object)
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name (:text mention-data)
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-mention-stickers
  [w]
  (reduce
    (fn [w* object]
      (let [tx (-> w*
                   (assoc :object object)
                   (mention-sticker-tx))]
        (-> w*
            (transact tx))))
    w
    (get-prefix-objects (assoc w
                               :prefix "usertag")))
  )

(defn extract-link-data
  [object]
  (let [text (some
                 (fn [obj]
                   (when (= (:type obj)
                            "text")
                     obj))
                 (:objects object))
        rect (some
               (fn [obj]
                 (when (= (:type obj)
                          "rect")
                   obj))
               (:objects object))
        image (some
                (fn [obj]
                  (when (= (:type obj)
                           "image")
                    obj))
                (:objects object))]
    {:text (:text object)
     :link (:link object)
     :scaleX 1
     :scaleY 1
     :top 0
     :left 0
     :type "link"
     :design (merge {:text-props
                     (select-keys text
                                  [:fontFamily
                                   :fontSize
                                   :top
                                   :left
                                   :fill])}
                    {:padding-x 180
                     :padding-y 40}
                    (select-keys rect
                                 [:rx
                                  :ry
                                  :opacity])
                    {:icon-props
                     (merge
                       {:left 30
                        :src (:path (uri/uri (:src image)))}
                       (select-keys image
                                    [:scaleX
                                     :scaleY]))})}
    ))

(defn link-sticker-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :link-sticker
      :asset/name "Link Sticker"
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (extract-link-data object)
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name "Link Sticker"
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-link-stickers
  [w]
  (if-let [object (some
                    (fn [object]
                      (when (= (:id object)
                               "link-sticker")
                        object))
                    (get-objects w))]
    (let [tx (-> w
                 (assoc :object object)
                 (link-sticker-tx))]
      (-> w
          (transact tx)))
    w)
  )

(defn extract-location-data
  [object]
  (let [text (some
               (fn [obj]
                 (when (= (:type obj)
                          "text")
                   obj))
               (:objects object))
        rect (some
               (fn [obj]
                 (when (= (:type obj)
                          "rect")
                   obj))
               (:objects object))
        image (some
                (fn [obj]
                  (when (= (:type obj)
                           "image")
                    obj))
                (:objects object))]
    {:location (:location object)
     :design (merge {:text-props
                     (select-keys text
                                  [:fontFamily
                                   :fontSize
                                   :top
                                   :left
                                   :fill])}
                    {:padding-x 150
                     :padding-y 40}
                    (select-keys rect
                                 [:rx
                                  :ry
                                  :opacity])
                    {:icon-props
                     (merge
                       {:left 20
                        :src (:path (uri/uri (:src image)))}
                       (select-keys image
                                    [:scaleX
                                     :scaleY]))})}
    ))

(defn location-sticker-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :location-sticker
      :asset/name "Location Sticker"
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (extract-location-data object)
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name "Location Sticker"
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-location-stickers
  [w]
  (if-let [object (some
                    (fn [object]
                      (when (= (:id object)
                               "location-sticker")
                        object))
                    (get-objects w))]
    (let [tx (-> w
                 (assoc :object object)
                 (location-sticker-tx))]
      (-> w
          (transact tx)))
    w)
  )

(defn question-sticker-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :question-sticker
      :asset/name "Question Sticker"
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (set/rename-keys (:question object)
                                   {:background_color :background-color
                                    :text_color :text-color})
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name "Question Sticker"
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-question-stickers
  [w]
  (if-let [object (some
                    (fn [object]
                      (when (= (:id object)
                               "question-sticker")
                        object))
                    (get-objects w))]
    (let [tx (-> w
                 (assoc :object object)
                 (question-sticker-tx))]
      (-> w
          (transact tx)))
    w)
  )

(defn quiz-sticker-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :quiz-sticker
      :asset/name "Quiz Sticker"
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (:quiz object)
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name "Quiz Sticker"
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-quiz-stickers
  [w]
  (if-let [object (some
                    (fn [object]
                      (when (= (:id object)
                               "quiz-sticker")
                        object))
                    (get-objects w))]
    (let [tx (-> w
                 (assoc :object object)
                 (quiz-sticker-tx))]
      (-> w
          (transact tx)))
    w)
  )

(defn emoji-slider-sticker-tx
  [{:keys [object] :as w}]
  (let [uuid (random-uuid)]
    [{:db/id "asset"
      :asset/uuid uuid
      :asset/type :emoji-slider-sticker
      :asset/name "Emoji Slider Sticker"
      :asset/width (:width object)
      :asset/height (:height object)
      :asset/data (set/rename-keys (:slider object)
                                   {:background_color :background-color
                                    :text_color :text-color})
      }

     (merge
       (clip-transform object)
       {:clip/uuid (random-uuid)
        :clip/asset "asset"
        :clip/name "Emoji-Slider Sticker"
        :clip/start 0
        :clip/end (get-slide-duration w)
        :clip/offset 0
        :clip/track "track"})
     {:db/id "track"
      :track/uuid (random-uuid)
      :track/object-id (:id object)}]))

(defn add-emoji-slider-stickers
  [w]
  (if-let [object (some
                    (fn [object]
                      (when (= (:id object)
                               "slider-sticker")
                        object))
                    (get-objects w))]
    (let [tx (-> w
                 (assoc :object object)
                 (emoji-slider-sticker-tx))]
      (-> w
          (transact tx)))
    w)
  )

(defn get-background-fill
  [w]
  (get-in w
          [:item
           :fabricjs-model
           :objects
           "background"
           :fill]))

(defn add-background-color
  [w]
  (let [fill (get-background-fill w)]
    (if (string? fill)
      (transact w
                [{:slide/uuid (get-in w
                                      [:slide
                                       :slide/uuid])
                  :slide/background-color fill}])
      w)))

(defn add-fabricjs-model
  [{:keys [item]
    :as w}]
  (js/Promise.
    (fn [resolve reject]
      (go
        (try
          (if (and (:main-video item)
                   (not (:fabricjs-model item)))
            (let [fabricjs-model (<! (editor.model/video-model+metadata
                                       {:story-media/blob-id (:blob (:main-video item))}
                                       editor.model/default-model-data))
                  fabricjs-model (merge fabricjs-model
                                        ;; Something od in v1. Some models are structured {:fabricjs-model {:canvas {:order []}}}
                                        ;; while others are {:fabricjs-model {:order []}}
                                        (:canvas fabricjs-model))]
              (resolve
                (assoc-in w
                          [:item :fabricjs-model]
                          fabricjs-model)))
            (resolve w))
          (catch :default e
            (reject e))
          )))))

(def prepare-fns
  [add-transit-str
   add-empty-db
   add-editor1
   add-item
   add-fabricjs-model
   add-slide
   add-main-video
   add-texts
   add-images
   add-blurry-background
   add-gif-videos
   add-gifs
   add-asset-durations
   add-measured-main-video-duration
   add-countdown
   add-poll-stickers
   add-hashtag-stickers
   add-mention-stickers
   add-link-stickers
   add-location-stickers
   add-question-stickers
   add-quiz-stickers
   add-emoji-slider-stickers
   add-background-color
   add-timeline])

(defn prepare-import
  [w]
  (common/achain
    w
    prepare-fns))

(comment
  (require 'ui.store
           'ui.editor2.timeline
           'ui.editor2.actions)

  (do
    (defn load-in-editor2!
      [w]
      (swap! ui.store/store
             assoc-in
             [:editor2]
             (merge
               defaults/defaults
               (select-keys w
                            [:db]))))

    (.then (prepare-import {:story-media/eid 17592186047491})
           (fn [w]
             (load-in-editor2! w)
             (ui.editor2.actions/prepare-assets! {:store ui.store/store})
             (tap> w)
             (def w
               w)))
    )

  (def hashtag
    (first
      (get-prefix-objects (assoc w
                                 :prefix "hashtag"))))

  (extract-hashtag-data hashtag)
  )
